/**
 * ClassificationIntelPanel — AI understanding panel for HubDetail.
 *
 * Replaces the previous flat `doctypePanel` + `rationaleToggle`
 * in HubDetail with a structured AI intelligence surface that
 * communicates:
 *
 *   1. What the AI classified this document as.
 *   2. How confident it is (visual bar + percentage badge).
 *   3. Why — an "Evidence basis" excerpt from the LLM rationale,
 *      with a full-text expand for the complete reasoning trace.
 *   4. Any warnings or errors from the classification pipeline.
 *
 * Design principles:
 *   - AI annotation surface: subtle violet-tinted background signals
 *     "this content was generated by AI, not the writer".
 *   - Confidence bar is the primary visual cue — colour + fill
 *     communicate tier without requiring the writer to parse a %.
 *   - Rationale excerpt is shown by default (first 200 chars) with
 *     a non-disruptive expand affordance. Writers can skip it
 *     entirely; it never gates the verify action.
 *   - The doctype selector preserves its existing a11y + testid
 *     contract so the verify workflow is unaffected.
 *
 * Props flow: all state lives in HubDetail; this panel is
 * fully controlled (no internal classification state). The only
 * local state is showFullRationale (UI-only expand toggle).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { aiColors } from "../tokens";
import type { DocumentRow } from "../types";
import { ConfidenceBar, confColor } from "./ConfidenceBar";

// Number of characters shown in the rationale excerpt before
// the "View full AI reasoning" expand affordance appears.
const RATIONALE_EXCERPT_LEN = 200;

export interface ClassificationIntelPanelProps {
  row: DocumentRow;
  /** Current (possibly writer-edited) source doctype value. */
  localSourceDoctype: string;
  /** Available doctype options for the change selector. */
  docTypeOptions: readonly string[];
  /** Called when writer selects a different doctype. */
  onDoctypeChange: (doctype: string) => void;
}

const useStyles = makeStyles({
  root: {
    backgroundColor: aiColors.annotationBg,
    border: `1px solid ${aiColors.annotationBorder}`,
    borderRadius: "5px",
    overflow: "hidden",
    marginBottom: "10px",
  },
  // ── Panel header ──────────────────────────────────────────────
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    padding: "7px 10px 5px",
    borderBottom: `1px solid ${aiColors.annotationBorder}`,
  },
  aiIcon: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: aiColors.thinking,
    flexShrink: 0,
    // Tiny static dot — the animation is on the chip in DocRow,
    // not here; this panel is a terminal display.
  },
  headerLabel: {
    flex: 1,
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
  },
  confBadge: {
    fontSize: "9px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    padding: "1px 5px",
    borderRadius: "9999px",
    letterSpacing: "0.3px",
    border: "1px solid transparent",
  },
  // ── Confidence bar ────────────────────────────────────────────
  barSection: {
    padding: "6px 10px 4px",
    borderBottom: `1px solid ${aiColors.annotationBorder}`,
  },
  barLabels: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "4px",
    fontSize: "7.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
  },
  // ── Doctype section ───────────────────────────────────────────
  doctypeSection: {
    padding: "8px 10px 8px",
    borderBottom: `1px solid ${aiColors.annotationBorder}`,
  },
  doctypeValueRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
  },
  doctypeValue: {
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
    fontFamily: fonts.display,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  changeSelect: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    backgroundColor: "transparent",
    border: `1px solid ${aiColors.annotationBorder}`,
    borderRadius: "3px",
    padding: "2px 5px",
    cursor: "pointer",
    flexShrink: 0,
    // Remove default arrow appearance; we keep it accessible.
    appearance: "none" as unknown as undefined,
    WebkitAppearance: "none" as unknown as undefined,
  },
  changeLabel: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
    marginTop: "2px",
  },
  // ── Error / warning banners ───────────────────────────────────
  errorBanner: {
    margin: "6px 10px 4px",
    padding: "6px 8px",
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    borderRadius: "3px",
    fontSize: "10px",
    color: statusColors.danger,
    fontFamily: fonts.body,
  },
  warningRow: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "6px",
    margin: "5px 10px 4px",
    fontSize: "10px",
    color: "#7C2D12",
    fontFamily: fonts.body,
  },
  warningDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    backgroundColor: statusColors.danger,
    marginTop: "3px",
    flexShrink: 0,
  },
  // ── Rationale / Evidence basis ────────────────────────────────
  rationaleSection: {
    padding: "8px 10px",
  },
  rationaleHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    marginBottom: "5px",
  },
  rationaleLabel: {
    fontSize: "8px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  rationaleQuote: {
    fontSize: "10.5px",
    fontFamily: fonts.body,
    color: inkColors.ink2,
    lineHeight: 1.55,
    borderLeft: `2px solid ${aiColors.annotationBorder}`,
    paddingLeft: "8px",
    marginLeft: "2px",
    fontStyle: "italic",
  },
  rationaleToggle: {
    marginTop: "6px",
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    display: "block",
    ":hover": { textDecoration: "underline" },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  noclassificationHint: {
    padding: "10px",
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    fontStyle: "italic",
  },
});

export const ClassificationIntelPanel: React.FC<ClassificationIntelPanelProps> = ({
  row,
  localSourceDoctype,
  docTypeOptions,
  onDoctypeChange,
}) => {
  const styles = useStyles();
  const [showFullRationale, setShowFullRationale] = useState(false);

  const sc = row.source_classification;
  const conf = sc?.source_doctype_confidence ?? null;
  const confPct = typeof conf === "number" ? Math.round(conf * 100) : null;
  const showLowConfWarning = !row.verified && typeof conf === "number" && conf < 0.6;
  const rationale = sc?.rationale ?? null;
  const isExcerpt = rationale !== null && rationale.length > RATIONALE_EXCERPT_LEN;
  const rationaleText =
    rationale === null
      ? null
      : !showFullRationale && isExcerpt
        ? `${rationale.slice(0, RATIONALE_EXCERPT_LEN)}…`
        : rationale;

  // When there is no source classification yet, still show the
  // (possibly writer-edited) doctype so the writer can see what
  // document type is assigned, and can change it via the selector.
  if (!sc) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <span className={styles.aiIcon} aria-hidden="true" />
          <span className={styles.headerLabel}>AI Classification</span>
        </div>
        <div className={styles.doctypeSection}>
          <div className={styles.changeLabel}>Source document type</div>
          <select
            value={localSourceDoctype}
            onChange={(e) => onDoctypeChange(e.target.value)}
            className={styles.changeSelect}
            aria-label="Change source doctype"
            data-testid="hub-detail-doctype-select"
            style={{ width: "100%" }}
          >
            {docTypeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
            {!docTypeOptions.includes(localSourceDoctype) && (
              <option value={localSourceDoctype}>{localSourceDoctype}</option>
            )}
          </select>
        </div>
        <div className={styles.noclassificationHint}>Classification not yet available for this document.</div>
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid="classification-intel-panel">
      {/* ── Header ── */}
      <div className={styles.header}>
        <span className={styles.aiIcon} aria-hidden="true" />
        <span className={styles.headerLabel}>AI Classification</span>
        {confPct !== null && (
          <span
            className={styles.confBadge}
            style={{
              color: confColor(conf ?? 0),
              borderColor: `${confColor(conf ?? 0)}44`,
              backgroundColor: `${confColor(conf ?? 0)}12`,
            }}
          >
            {confPct}%
          </span>
        )}
      </div>

      {/* ── Confidence bar ── */}
      {typeof conf === "number" && (
        <div className={styles.barSection}>
          <ConfidenceBar confidence={conf} size="md" />
          <div className={styles.barLabels}>
            <span>Low confidence</span>
            <span>High confidence</span>
          </div>
        </div>
      )}

      {/* ── Doctype value + change selector ── */}
      <div className={styles.changeLabel}>Source document type</div>

      <div className={styles.doctypeSection}>
        <select
          value={localSourceDoctype}
          onChange={(e) => onDoctypeChange(e.target.value)}
          className={styles.changeSelect}
          aria-label="Change source doctype"
          data-testid="hub-detail-doctype-select"
          style={{ width: "100%" }}
        >
          {docTypeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
          {!docTypeOptions.includes(localSourceDoctype) && (
            <option value={localSourceDoctype}>{localSourceDoctype}</option>
          )}
        </select>
      </div>

      {/* ── Error banner ── */}
      {sc.error && (
        <div className={styles.errorBanner} role="alert" data-testid="hub-detail-error">
          {sc.error}
        </div>
      )}

      {/* ── Low confidence warning ── */}
      {showLowConfWarning && (
        <div className={styles.warningRow} role="alert">
          <span className={styles.warningDot} aria-hidden="true" />
          <span>Low confidence — please review and confirm the doctype.</span>
        </div>
      )}

      {/* ── Rationale / evidence basis ── */}
      {rationaleText && (
        <div className={styles.rationaleSection}>
          <div className={styles.rationaleHeader}>
            <span className={styles.rationaleLabel}>Evidence basis</span>
          </div>
          <div className={styles.rationaleQuote}>{rationaleText}</div>
          {isExcerpt && (
            <button
              type="button"
              className={styles.rationaleToggle}
              onClick={() => setShowFullRationale((v) => !v)}
              aria-expanded={showFullRationale}
              data-testid="hub-detail-rationale-toggle"
            >
              {showFullRationale ? "▾ Collapse reasoning" : "▸ View full AI reasoning"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassificationIntelPanel;
